import { Suspense, useEffect, useState, type CSSProperties, type Dispatch, type SetStateAction } from 'react'
import { Navigate, Outlet, useOutletContext } from 'react-router-dom'
import { apiHub, apiHubInicio, mergeHubInicio } from '../lib/api'
import { clearAuth, getStoredUser, isLideranca } from '../lib/auth'
import { LOGIN_PATH } from '../lib/hubPath'
import { schedulePrefetchCriticalHubRoutes } from '../lib/routeChunks'
import { schedulePrefetchViralHubData } from '../lib/prefetchViral'
import { applyHubPwaBranding } from '../lib/pwaBranding'
import { ensureGtm } from '../lib/gtm'
import { peekHubShellCache } from '../lib/hubShellCache'
import type { HubPayload } from '../types'
import { BottomNav } from './BottomNav'
import { HubErrorBoundary } from './HubErrorBoundary'
import { HubPageSkeleton } from './HubPageSkeleton'
import { HubShellSkeleton } from './HubShellSkeleton'
import { SideNav } from './SideNav'

export type HubOutletContext = {
  hub: HubPayload | null
  setHub: Dispatch<SetStateAction<HubPayload | null>>
  error: string
  setError: (error: string) => void
}

export function useHubContext(): HubOutletContext {
  return useOutletContext<HubOutletContext>()
}

function themeToCssVars(theme: NonNullable<HubPayload['theme']>): CSSProperties {
  return {
    ['--bp-primary' as string]: theme.primary,
    ['--bp-secondary' as string]: theme.secondary,
    ['--bp-hover' as string]: theme.hover,
    ['--bp-active' as string]: theme.active,
  }
}

export function HubLayout() {
  const user = getStoredUser()
  const userId = user?.id ?? 0
  const [hub, setHub] = useState<HubPayload | null>(() => (userId > 0 ? peekHubShellCache(userId) : null))
  const [error, setError] = useState('')

  useEffect(() => {
    const current = getStoredUser()
    if (!userId || !isLideranca(current)) return
    let cancelled = false
    ;(async () => {
      try {
        const shell = await apiHub({ force: true })
        if (cancelled) return
        setError('')
        setHub(shell)
        schedulePrefetchViralHubData()
        try {
          const inicio = await apiHubInicio()
          if (cancelled) return
          // Mesclar no hub atual — se o usuário já trocou a foto enquanto /inicio
          // carregava, não sobrescrever com o shell antigo da closure.
          setHub((prev) => mergeHubInicio(prev ?? shell, inicio))
        } catch (inicioErr) {
          // Shell já pintou nav; home mostra erro pontual se preciso.
          if (!cancelled) {
            setError(inicioErr instanceof Error ? inicioErr.message : 'Erro ao carregar o início')
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    if (!hub?.theme) return
    const root = document.documentElement
    root.style.setProperty('--bp-primary', hub.theme.primary)
    root.style.setProperty('--bp-secondary', hub.theme.secondary)
    root.style.setProperty('--bp-hover', hub.theme.hover)
    root.style.setProperty('--bp-active', hub.theme.active)
    return () => {
      root.style.removeProperty('--bp-primary')
      root.style.removeProperty('--bp-secondary')
      root.style.removeProperty('--bp-hover')
      root.style.removeProperty('--bp-active')
    }
  }, [hub?.theme])

  useEffect(() => {
    if (hub?.campanha.gtm) ensureGtm(hub.campanha.gtm)
  }, [hub?.campanha.gtm])

  useEffect(() => {
    if (!hub) return
    schedulePrefetchCriticalHubRoutes()
  }, [hub])

  useEffect(() => {
    if (!hub?.pwa) return
    applyHubPwaBranding(hub.pwa)
  }, [hub?.pwa])

  if (!user || !isLideranca(user)) {
    return <Navigate to={LOGIN_PATH} replace />
  }

  if (error && !hub) {
    return (
      <div className="bp-page">
        <div className="bp-card">
          <p className="bp-error">{error}</p>
          <button
            type="button"
            className="bp-btn bp-btn--primary"
            onClick={() => {
              clearAuth()
              window.location.href = LOGIN_PATH
            }}
          >
            Fazer login
          </button>
        </div>
      </div>
    )
  }

  if (!hub) {
    return <HubShellSkeleton />
  }

  const themeStyle = hub.theme ? themeToCssVars(hub.theme) : undefined

  return (
    <div className="bp-shell" style={themeStyle}>
      <SideNav
        bottomNav={hub.bottom_nav}
        menus={hub.menus}
        campanhaNome={hub.campanha.nome}
        campanhaLogo={hub.campanha.logo}
        brandNome={hub.brand?.nome}
        brandLogo={hub.brand?.logo}
        brandUrl={hub.brand?.url}
      />
      <div className="bp-page">
        <HubErrorBoundary>
          <Suspense fallback={<HubPageSkeleton />}>
            <Outlet context={{ hub, setHub, error, setError } satisfies HubOutletContext} />
          </Suspense>
        </HubErrorBoundary>
      </div>
      <BottomNav items={hub.bottom_nav} />
    </div>
  )
}
